import type { GetServerSideProps } from "next"
import type { NextPageWithLayout } from "common/types/page"
import { Layout as NewslettersPageLayout } from "pages/newsletters/layout"
import { AppProps, getAppServerSideProps } from "common/lib/getAppServerSideProps"
import { useRememberRegion } from "common/hooks/data/useRememberRegion"
import { MetaAttributes } from "common/components/MetaAttributes"
import { useTranslations } from "modules/i18n/hooks/useTranslations"
import { gql } from "common/lib/gql"
import getConfig from "next/config"
import { fetchGraphqlQuery } from "common/lib/fetchGraphqlQuery"
import { Group, NewslettersPageQuery, PageTypeEnum } from "common/types/graphql"
import { tracer } from "common/lib/tracer"
import { useNewsletterGroups } from "modules/newsletters/hooks/useNewsletterGroups"
import { useRegionConfig } from "modules/i18n/hooks/useRegionConfig"
import { useExperiment } from "common/lib/abTesting"
import { useViewer } from "common/hooks/data/useViewer"
import { trackPageViewed } from "common/lib/tcEventCollector"
import { useEffect } from "react"
import dynamic from "next/dynamic"

const NewslettersMain = dynamic(
  () => import("modules/newsletters/components/NewslettersMain").then((mod) => mod.NewslettersMain),
  {
    ssr: false,
  },
)

const NewslettersMainV2 = dynamic(
  () =>
    import("modules/newsletters-v2/components/NewslettersMain").then((mod) => mod.NewslettersMain),
  {
    ssr: false,
  },
)

export interface NewslettersPageProps extends AppProps {
  groups: Group[]
}

const QUERY = gql`
  query newslettersPage($regionCode: String!) {
    newslettersPage(regionCode: $regionCode) {
      cacheKey
      groups {
        name
        groupItems {
          id
          name
          promoted
          frequency
          description
          pitch
          previewUrl
          heroImageUrl
          thumbnailImageUrl
        }
      }
    }
  }
`

export const getServerSideProps: GetServerSideProps<NewslettersPageProps> = async ({
  query,
  res,
  req,
}) => {
  const appProps = await getAppServerSideProps({ query, res, req })
  const { serverRuntimeConfig } = getConfig()
  const { regionCode } = appProps

  let headers: { Authorization: string; User?: string } = {
    Authorization: `Bearer ${serverRuntimeConfig.TC_FRONTEND_TOKEN}`,
  }

  const { newslettersPage } = await fetchGraphqlQuery<NewslettersPageQuery>(
    QUERY,
    headers,
    { regionCode },
    tracer,
  )

  res.setHeader("Cache-Control", "no-store")
  res.setHeader("Surrogate-Control", "max-age=14400") // Newsletter sub page expires in 4 hours
  res.setHeader("Surrogate-Key", newslettersPage?.cacheKey || "")

  return {
    props: { ...appProps, groups: newslettersPage?.groups || [], pageType: PageTypeEnum.Resource },
  }
}

const NewslettersPage: NextPageWithLayout<NewslettersPageProps> = () => {
  useRememberRegion()
  const {
    social_media: { facebook_url: facebookURL },
  } = useRegionConfig()
  const t = useTranslations("newsletters")
  const { promotedNewsletter } = useNewsletterGroups()
  const { isSignedIn } = useViewer()
  const { variantId } = useExperiment("01JFXQ8ZY6WNBVP2KR3MXHQ4JT", { isSignedIn })

  useEffect(() => {
    trackPageViewed({
      urlPath: window.location.pathname,
      userLoggedIn: isSignedIn,
      category: "newsletters",
    })
    // We intentionally track page views only once on mount, so this `useEffect` hook will not have any dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <MetaAttributes
        title={t("meta.title")}
        description={t("meta.description")}
        ogTitle={t("meta.title")}
        ogDescription={t("meta.description")}
        articlePublisher={facebookURL}
        image={promotedNewsletter?.heroImageUrl}
      />

      {variantId === "01JFXQ8ZY6A7N8P9Q2R5STUVWX" ? <NewslettersMainV2 /> : <NewslettersMain />}
    </>
  )
}

NewslettersPage.getLayout = (page) => <NewslettersPageLayout>{page}</NewslettersPageLayout>

export default NewslettersPage
